import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Characters.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

const VIEW_LABELS = ["Character Sheet"];

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="cs-modal__image" />
        <span className="cs-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* --- Main page --- */
export default function Characters() {
  // Characters
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState("");

  // Wardrobe
  const [wardrobe, setWardrobe] = useState({ garments: [], accessories: [] });

  // Selected pieces for outfit
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);

  // Saved sheets for selected character
  const [savedSheets, setSavedSheets] = useState([]);

  // Generation
  const [status, setStatus] = useState("idle");
  const [views, setViews] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalView, setModalView] = useState(null);
  const [sheetModal, setSheetModal] = useState(null);
  const pollingRef = useRef(false);

  // Reel scroll refs
  const charsReelRef = useRef(null);
  const garmentsReelRef = useRef(null);
  const accessoriesReelRef = useRef(null);
  const sheetsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const canGenerate = selectedChar && (selectedGarments.length + selectedAccessories.length) >= 1 && status !== "generating";

  // Load characters (with face URLs) + wardrobe on mount
  useEffect(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    fetch(`${API_URL}/characters`, { headers })
      .then((r) => r.json())
      .then(async (d) => {
        const chars = d.characters || [];
        // chars is now [{id, display_name}, ...] — fetch face URL for each
        const detailed = await Promise.all(
          chars.map(async (c) => {
            const cid = c.id || c; // backwards compat if still string
            const displayName = c.display_name || "";
            try {
              const res = await fetch(`${API_URL}/characters/${cid}`, { headers });
              const detail = await res.json();
              const faceUrl = detail?.face?.reference?.[0]?.url || null;
              return { id: cid, displayName: detail.display_name || displayName, faceUrl };
            } catch {
              return { id: cid, displayName: displayName, faceUrl: null };
            }
          })
        );
        setCharacters(detailed);
      })
      .catch(() => {});
    fetch(`${API_URL}/wardrobe`, { headers })
      .then((r) => r.json())
      .then((d) => setWardrobe({ garments: d.garments || [], accessories: d.accessories || [] }))
      .catch(() => {});
  }, []);

  // Load saved sheets when character changes
  useEffect(() => {
    if (!selectedChar) { setSavedSheets([]); return; }
    fetch(`${API_URL}/characters/${selectedChar}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.json())
      .then((d) => setSavedSheets(d.sheets || []))
      .catch(() => setSavedSheets([]));
  }, [selectedChar]);

  const toggleGarment = (path) => {
    setSelectedGarments((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const toggleAccessory = (path) => {
    setSelectedAccessories((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const getPathFromUrl = (url) => {
    const marker = "/characters/";
    const idx = url.indexOf(marker);
    return idx >= 0 ? url.substring(idx + marker.length) : url;
  };

  const pollStatus = useCallback(async (jid) => {
    pollingRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollingRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/character-sheet/${encodeURIComponent(jid)}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();

        if (data.status === "completed") {
          const resultRes = await fetch(
            `${API_URL}/character-sheet/${encodeURIComponent(jid)}/result`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          const result = await resultRes.json();
          const viewsData = (result.views || []).map((v) => ({
            ...v,
            image: v.image || null,
          }));
          setViews(viewsData);
          setStatus("done");
          pollingRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollingRef.current = false;
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollingRef.current = false;
  }, []);

  const handleGenerate = async () => {
    setStatus("generating");
    setViews([]);
    setErrorMsg("");

    const garmentPaths = selectedGarments.map(getPathFromUrl);
    const accessoryPaths = selectedAccessories.map(getPathFromUrl);

    try {
      const res = await fetch(`${API_URL}/character-sheet/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          character_id: selectedChar,
          garment_paths: garmentPaths,
          accessory_paths: accessoryPaths,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setLastJobId(data.job_id);
      pollStatus(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al enviar");
      setStatus("error");
    }
  };

  const [lastJobId, setLastJobId] = useState("");
  const [saving, setSaving] = useState(false);

  const downloadImage = (base64, viewName) => {
    const link = document.createElement("a");
    link.href = base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
    link.download = `character_sheet_${selectedChar}_${viewName}.png`;
    link.click();
  };

  const handleSave = async () => {
    if (!lastJobId || !selectedChar) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/character-sheet/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ job_id: lastJobId, character_id: selectedChar }),
      });
      if (res.ok) {
        // Reload saved sheets
        const detail = await fetch(`${API_URL}/characters/${selectedChar}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }).then((r) => r.json());
        setSavedSheets(detail.sheets || []);
      }
    } catch {}
    setSaving(false);
  };

  // --- Rename sheet ---
  const handleRenameSheet = async (oldName, newName) => {
    const clean = newName.trim();
    if (!clean || clean === oldName.replace(".png", "").replace(/_/g, " ")) return;
    try {
      const res = await fetch(`${API_URL}/character-sheet/${selectedChar}/${encodeURIComponent(oldName)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: clean.replace(/ /g, "_") }),
      });
      if (!res.ok) throw new Error("Error al renombrar");
      // Reload sheets
      const detail = await fetch(`${API_URL}/characters/${selectedChar}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then((r) => r.json());
      setSavedSheets(detail.sheets || []);
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const getViewSrc = (view) => {
    if (!view?.image) return null;
    return view.image.startsWith("data:") ? view.image : `data:image/png;base64,${view.image}`;
  };

  // Readable name from char ID: char_m001_hombre_1_e96d -> M001 Hombre 1
  const charDisplayName = (id) => {
    const parts = id.replace("char_", "").split("_");
    parts.pop(); // remove hex suffix
    return parts.map((p, i) => i === 0 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  };

  return (
    <div className="characters-page">
      {/* LEFT — Select character + wardrobe */}
      <div className="cs-panel cs-panel--left">
        <div className="cs-result-header">
          <h2 className="cs-panel__title">Character Sheet</h2>
          <button
            className={`cs-btn-generate ${status === "generating" ? "cs-btn-generate--loading" : ""}`}
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {status === "generating" ? "Generando..." : "Generar"}
          </button>
        </div>

        {/* Character selector — reel */}
        <section className="cs-section">
          <span className="cs-section__title">Personaje</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={charsReelRef}>
              {characters.map((c) => (
                <div
                  key={c.id}
                  className={`cs-reel-item ${selectedChar === c.id ? "cs-reel-item--selected" : ""}`}
                  onClick={() => { setSelectedChar(c.id); setSelectedGarments([]); setSelectedAccessories([]); setViews([]); setStatus("idle"); }}
                >
                  {c.faceUrl ? (
                    <img src={c.faceUrl} alt={c.id} />
                  ) : (
                    <div className="cs-reel-item__placeholder" />
                  )}
                  <span className="cs-reel-item__name">{c.displayName || charDisplayName(c.id)}</span>
                </div>
              ))}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Wardrobe — Prendas reel */}
        <section className="cs-section">
          <span className="cs-section__title">Prendas</span>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(garmentsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={garmentsReelRef}>
              {wardrobe.garments.map((g) => (
                <div
                  key={g.url}
                  className={`cs-reel-item cs-reel-item--wide ${selectedGarments.includes(g.url) ? "cs-reel-item--selected" : ""}`}
                  onClick={() => toggleGarment(g.url)}
                >
                  <img src={g.url} alt={g.name} />
                  <span className="cs-reel-item__name">{g.name.replace(".png", "").replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(garmentsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Wardrobe — Complementos reel */}
        {wardrobe.accessories.length > 0 && (
          <section className="cs-section">
            <span className="cs-section__title">Complementos</span>
            <div className="cs-reel cs-reel--tall">
              <button className="cs-reel__btn" onClick={() => scrollReel(accessoriesReelRef, -1)}>{"\u25C0"}</button>
              <div className="cs-reel__frames" ref={accessoriesReelRef}>
                {wardrobe.accessories.map((a) => (
                  <div
                    key={a.url}
                    className={`cs-reel-item cs-reel-item--wide ${selectedAccessories.includes(a.url) ? "cs-reel-item--selected" : ""}`}
                    onClick={() => toggleAccessory(a.url)}
                  >
                    <img src={a.url} alt={a.name} />
                    <span className="cs-reel-item__name">{a.name.replace(".png", "").replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
              <button className="cs-reel__btn" onClick={() => scrollReel(accessoriesReelRef, 1)}>{"\u25B6"}</button>
            </div>
          </section>
        )}

        {/* Saved sheets reel */}
        {selectedChar && (
          <section className="cs-section">
            <span className="cs-section__title">Hojas guardadas</span>
            <div className="cs-reel">
              <button className="cs-reel__btn" onClick={() => scrollReel(sheetsReelRef, -1)}>{"\u25C0"}</button>
              <div className="cs-reel__frames" ref={sheetsReelRef}>
                {savedSheets.length > 0 ? (
                  savedSheets.map((s) => (
                    <div key={s.url} className="cs-reel-item cs-reel-item--sheet" onClick={() => setSheetModal(s.url)}>
                      <img src={s.url} alt={s.name} />
                      <input
                        className="cs-reel-item__name cs-sheet-name-input"
                        defaultValue={s.name.replace(".png", "").replace(/_/g, " ")}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => handleRenameSheet(s.name, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                      />
                    </div>
                  ))
                ) : (
                  <span className="cs-reel-empty">Genera y pulsa Guardar</span>
                )}
              </div>
              <button className="cs-reel__btn" onClick={() => scrollReel(sheetsReelRef, 1)}>{"\u25B6"}</button>
            </div>
          </section>
        )}

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Result */}
      <div className="cs-panel cs-panel--right">
        <h2 className="cs-panel__title">Resultado</h2>

        {status === "idle" && views.length === 0 && (
          <div className="cs-results-empty">
            <p>Selecciona personaje y prendas, luego pulsa Generar</p>
          </div>
        )}

        <div className="cs-results-grid">
          {VIEW_LABELS.map((label, i) => {
            const view = views[i];
            const src = view ? getViewSrc(view) : null;
            return (
              <div key={label} className="cs-result-card">
                <div
                  className={`cs-result-card__image ${src ? "cs-result-card__image--loaded" : ""}`}
                  onClick={() => src && setModalView(i)}
                >
                  {src ? (
                    <img src={src} alt={label} />
                  ) : (
                    <div className="cs-result-card__placeholder">
                      {status === "generating" && <span className="cs-spinner" />}
                    </div>
                  )}
                </div>
                <div className="cs-result-card__footer">
                  <span className="cs-result-card__label">{label}</span>
                  <div className="cs-result-card__actions">
                    {src && (
                      <button
                        className="cs-btn-save"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? "Guardando..." : "Guardar"}
                      </button>
                    )}
                    {src && (
                      <button
                        className="cs-result-card__download"
                        onClick={() => downloadImage(view.image, view.view || label.toLowerCase())}
                      >
                        &#x2B73;
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalView !== null && views[modalView] && (
        <ImageModal
          src={getViewSrc(views[modalView])}
          label={VIEW_LABELS[modalView]}
          onClose={() => setModalView(null)}
        />
      )}

      {sheetModal && (
        <ImageModal
          src={sheetModal}
          label="Hoja guardada"
          onClose={() => setSheetModal(null)}
        />
      )}
    </div>
  );
}
